/* eslint-disable no-bitwise -- zip headers are packed bit fields, they can only be read this way */

import type { TypeZipEntry } from './types';

// ----------------------------------------------------------------------------

const SIGNATURE_EOCD = 0x06054b50;
const SIGNATURE_EOCD64 = 0x06064b50;
const SIGNATURE_EOCD64_LOCATOR = 0x07064b50;
const SIGNATURE_CENTRAL = 0x02014b50;

const SIZE_EOCD = 22;
const SIZE_EOCD64 = 56;
const SIZE_EOCD64_LOCATOR = 20;
const SIZE_CENTRAL = 46;

const MAX_COMMENT = 0xffff;
const MASK_16 = 0xffff;
const MASK_32 = 0xffffffff;

const FLAG_ENCRYPTED = 0x0001;

const EXTRA_ID_ZIP64 = 0x0001;

// One decoder for the whole module: a large zip has tens of thousands of names to decode.
const decoder = new TextDecoder('utf-8');

// ----------------------------------------------------------------------------

type TypeDirectoryLocation = {
    entries: number;
    size: number;
    offset: number;
};

type TypeSizeValues = {
    size: number;
    compressedSize: number;
    localHeaderOffset: number;
};

// ----------------------------------------------------------------------------

/*
 * Reads the central directory of a zip, without decompressing anything. Only the tail and the
 * directory itself are read from the blob, so listing a large zip stays cheap.
 */
async function readEntries(blob: Blob): Promise<TypeZipEntry[]> {
    const tailSize = Math.min(blob.size, SIZE_EOCD + MAX_COMMENT + SIZE_EOCD64_LOCATOR);

    if (tailSize < SIZE_EOCD) {
        throw new Error('Invalid zip file: too small.');
    }

    const tailStart = blob.size - tailSize;
    const tail = new DataView(await blob.slice(tailStart).arrayBuffer());

    const eocdOffset = findEndOfCentralDirectory(tail);

    if (eocdOffset < 0) {
        throw new Error('Invalid zip file: end of central directory not found.');
    }

    const location = await readDirectoryLocation(blob, tail, eocdOffset);

    // A zip made from an empty folder has a zero-length central directory: valid, just empty.
    if (location.entries === 0 && location.size === 0) {
        return [];
    }

    if (location.size <= 0 || location.offset < 0 || location.offset + location.size > blob.size) {
        throw new Error('Invalid zip file: central directory is out of range.');
    }

    const central = new DataView(await blob.slice(location.offset, location.offset + location.size).arrayBuffer());

    const entries: TypeZipEntry[] = [];
    let cursor = 0;

    while (cursor + SIZE_CENTRAL <= central.byteLength) {
        if (central.getUint32(cursor, true) !== SIGNATURE_CENTRAL) {
            break;
        }

        const record = readCentralRecord(central, cursor);

        if (!record) {
            break;
        }

        entries.push(record.entry);
        cursor = record.next;
    }

    return entries;
}

// ----------------------------------------------------------------------------

/*
 * The signature can also show up inside the trailing comment, so a candidate is only trusted when
 * its comment length matches the remaining bytes. The first plain match is the fallback.
 */
function findEndOfCentralDirectory(view: DataView): number {
    let fallback = -1;

    for (let offset = view.byteLength - SIZE_EOCD; offset >= 0; offset--) {
        if (view.getUint32(offset, true) !== SIGNATURE_EOCD) {
            continue;
        }

        const commentLength = view.getUint16(offset + 20, true);

        if (offset + SIZE_EOCD + commentLength === view.byteLength) {
            return offset;
        }

        if (fallback < 0) {
            fallback = offset;
        }
    }

    return fallback;
}

async function readDirectoryLocation(blob: Blob, tail: DataView, eocdOffset: number): Promise<TypeDirectoryLocation> {
    const location: TypeDirectoryLocation = {
        entries: tail.getUint16(eocdOffset + 10, true),
        size: tail.getUint32(eocdOffset + 12, true),
        offset: tail.getUint32(eocdOffset + 16, true),
    };

    const needsZip64 = location.entries === MASK_16 || location.size === MASK_32 || location.offset === MASK_32;
    const locatorOffset = eocdOffset - SIZE_EOCD64_LOCATOR;

    if (!needsZip64 || locatorOffset < 0 || tail.getUint32(locatorOffset, true) !== SIGNATURE_EOCD64_LOCATOR) {
        return location;
    }

    const eocd64Offset = Number(tail.getBigUint64(locatorOffset + 8, true));
    const eocd64 = new DataView(await blob.slice(eocd64Offset, eocd64Offset + SIZE_EOCD64).arrayBuffer());

    if (eocd64.byteLength < SIZE_EOCD64 || eocd64.getUint32(0, true) !== SIGNATURE_EOCD64) {
        return location;
    }

    location.entries = Number(eocd64.getBigUint64(32, true));
    location.size = Number(eocd64.getBigUint64(40, true));
    location.offset = Number(eocd64.getBigUint64(48, true));

    return location;
}

function readCentralRecord(view: DataView, offset: number): { entry: TypeZipEntry; next: number } | null {
    const flags = view.getUint16(offset + 8, true);
    const method = view.getUint16(offset + 10, true);
    const time = view.getUint16(offset + 12, true);
    const date = view.getUint16(offset + 14, true);
    const crc32 = view.getUint32(offset + 16, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);

    const nameStart = offset + SIZE_CENTRAL;
    const extraStart = nameStart + nameLength;
    const next = extraStart + extraLength + commentLength;

    if (next > view.byteLength) {
        return null;
    }

    const values: TypeSizeValues = {
        size: view.getUint32(offset + 24, true),
        compressedSize: view.getUint32(offset + 20, true),
        localHeaderOffset: view.getUint32(offset + 42, true),
    };
    readZip64Extra(view, extraStart, extraLength, values);

    const rawName = new Uint8Array(view.buffer, view.byteOffset + nameStart, nameLength);
    const rawPath = decodeName(rawName);
    const path = normalizePath(rawPath);

    return {
        entry: {
            path: path ?? rawPath,
            name: getEntryName(path ?? rawPath),
            size: values.size,
            compressedSize: values.compressedSize,
            method,
            crc32,
            flags,
            lastModified: readDosDateTime(date, time),
            localHeaderOffset: values.localHeaderOffset,
            isDirectory: rawPath.endsWith('/') || rawPath.endsWith('\\'),
            isEncrypted: (flags & FLAG_ENCRYPTED) !== 0,
            isUnsafePath: path === null,
        },
        next,
    };
}

/*
 * Only the fields flagged as 0xffffffff in the base record are stored in the zip64 extra field,
 * and always in this order.
 */
function readZip64Extra(view: DataView, start: number, length: number, values: TypeSizeValues): void {
    const end = Math.min(start + length, view.byteLength);
    let cursor = start;

    while (cursor + 4 <= end) {
        const id = view.getUint16(cursor, true);
        const dataSize = view.getUint16(cursor + 2, true);
        let field = cursor + 4;

        if (id === EXTRA_ID_ZIP64) {
            if (values.size === MASK_32 && field + 8 <= end) {
                values.size = Number(view.getBigUint64(field, true));
                field += 8;
            }
            if (values.compressedSize === MASK_32 && field + 8 <= end) {
                values.compressedSize = Number(view.getBigUint64(field, true));
                field += 8;
            }
            if (values.localHeaderOffset === MASK_32 && field + 8 <= end) {
                values.localHeaderOffset = Number(view.getBigUint64(field, true));
            }

            return;
        }

        cursor = cursor + 4 + dataSize;
    }
}

/*
 * Bit 11 marks utf-8 names, but the legacy alternative (cp437) has no TextDecoder label, so every
 * name is decoded as utf-8 — plain ascii names, the common case, come out identical either way.
 */
function decodeName(raw: Uint8Array): string {
    return decoder.decode(raw);
}

/*
 * Returns null for paths that would escape the extraction root (zip slip) or that are empty.
 * Also applied to the caller's root folder in `extract`, which is prefixed onto every path and
 * would otherwise bypass this guard.
 */
function normalizePath(name: string): string | null {
    const path = name
        .replace(/\\/g, '/')
        .replace(/\/{2,}/g, '/')
        .replace(/^\/+/, '')
        .replace(/\/+$/, '');

    if (path === '' || path.split('/').some((segment): boolean => segment === '..')) {
        return null;
    }

    return path;
}

function getEntryName(path: string): string {
    const slashIndex = path.lastIndexOf('/');

    return slashIndex >= 0 ? path.slice(slashIndex + 1) : path;
}

function readDosDateTime(date: number, time: number): number {
    if (date === 0 && time === 0) {
        return Date.now();
    }

    const year = ((date >> 9) & 0x7f) + 1980;
    const month = ((date >> 5) & 0x0f) - 1;
    const day = date & 0x1f;
    const hours = (time >> 11) & 0x1f;
    const minutes = (time >> 5) & 0x3f;
    const seconds = (time & 0x1f) * 2;

    const parsed = new Date(year, month, day, hours, minutes, seconds).getTime();

    return Number.isNaN(parsed) ? Date.now() : parsed;
}

// ----------------------------------------------------------------------------

export { normalizePath };

export default readEntries;
